export const DRAFT_STORAGE_PREFIX = 'invoice-admin:draft:';

export const DRAFT_CREATE_OWNER_PREFIX = 'invoice-admin:draft-owner:create:';

export const DEFAULT_DRAFT_VERSION = 1;

export const DEFAULT_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export interface DraftEnvelope<TData, TMeta = unknown> {
  v: number;
  savedAt: string;
  url: string;
  data: TData;
  meta?: TMeta;
}

function getStorage(storage?: Storage | null): Storage | null {
  return getBrowserStorage(storage, 'localStorage');
}

function getSessionStorage(storage?: Storage | null): Storage | null {
  return getBrowserStorage(storage, 'sessionStorage');
}

function getLegacyOwnerStorage(storage?: Storage | null): Storage | null {
  return getBrowserStorage(storage, 'localStorage');
}

function getBrowserStorage(
  storage: Storage | null | undefined,
  storageName: 'localStorage' | 'sessionStorage',
): Storage | null {
  if (storage === null) return null;
  if (storage) return storage;
  try {
    return globalThis[storageName];
  } catch {
    return null;
  }
}

function nowMs(now?: number | Date): number {
  if (now == null) return Date.now();
  return typeof now === 'number' ? now : now.getTime();
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

export function safeJsonStringify(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function safeRemove(storage: Storage, key: string): boolean {
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function safeGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage, key: string, value: string): boolean {
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function isQuotaExceededError(error: unknown): boolean {
  if (!error) return false;
  const anyError = error as { name?: unknown; code?: unknown; message?: unknown };
  const name = typeof anyError.name === 'string' ? anyError.name : '';
  const message = typeof anyError.message === 'string' ? anyError.message : '';
  const code = typeof anyError.code === 'number' ? anyError.code : null;

  if (name === 'QuotaExceededError') return true;
  if (name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
  if (code === 22 || code === 1014) return true;
  if (/quota/i.test(name) || /quota/i.test(message)) return true;
  return false;
}

export function draftKey(documentType: string, userId: string, docId?: string | null): string {
  const cleanedType = documentType.trim();
  const cleanedUser = userId.trim();
  const cleanedDoc = (docId ?? '').trim();

  if (!cleanedType) throw new Error('draftKey: documentType is required');
  if (!cleanedUser) throw new Error('draftKey: userId is required');

  return cleanedDoc
    ? `${DRAFT_STORAGE_PREFIX}${cleanedType}:${cleanedUser}:${cleanedDoc}`
    : `${DRAFT_STORAGE_PREFIX}${cleanedType}:${cleanedUser}`;
}

export function draftCreateOwnerKey(documentType: string, userId: string): string {
  const cleanedType = documentType.trim();
  const cleanedUser = userId.trim();

  if (!cleanedType) throw new Error('draftCreateOwnerKey: documentType is required');
  if (!cleanedUser) throw new Error('draftCreateOwnerKey: userId is required');

  return `${DRAFT_CREATE_OWNER_PREFIX}${cleanedType}:${cleanedUser}`;
}

export function readCreateDraftOwner(
  documentType: string,
  userId: string,
  options?: { storage?: Storage | null; legacyStorage?: Storage | null },
): string | null {
  const key = draftCreateOwnerKey(documentType, userId);
  const ownerStorage = getSessionStorage(options?.storage);
  const ownerValue = ownerStorage ? safeGet(ownerStorage, key) : null;
  const cleanedOwnerValue = ownerValue?.trim() ?? '';
  if (cleanedOwnerValue.length > 0) return cleanedOwnerValue;

  const legacyStorage = getLegacyOwnerStorage(options?.legacyStorage);
  if (!legacyStorage) return null;
  const legacyValue = safeGet(legacyStorage, key);
  const cleanedLegacyValue = legacyValue?.trim() ?? '';
  if (cleanedLegacyValue.length === 0) return null;

  if (ownerStorage && ownerStorage !== legacyStorage) {
    if (safeSet(ownerStorage, key, cleanedLegacyValue)) safeRemove(legacyStorage, key);
  }

  return cleanedLegacyValue;
}

export function writeCreateDraftOwner(
  documentType: string,
  userId: string,
  ownerId: string,
  options?: { storage?: Storage | null; legacyStorage?: Storage | null },
): boolean {
  const ownerStorage = getSessionStorage(options?.storage);
  if (!ownerStorage) return false;

  const cleanedOwner = ownerId.trim();
  if (!cleanedOwner) return false;

  const key = draftCreateOwnerKey(documentType, userId);
  const wrote = safeSet(ownerStorage, key, cleanedOwner);
  if (!wrote) return false;

  const legacyStorage = getLegacyOwnerStorage(options?.legacyStorage);
  if (legacyStorage && legacyStorage !== ownerStorage) {
    safeRemove(legacyStorage, key);
  }
  return true;
}

export function clearCreateDraftOwner(
  documentType: string,
  userId: string,
  options?: { storage?: Storage | null; legacyStorage?: Storage | null; expectedOwnerId?: string },
): void {
  const key = draftCreateOwnerKey(documentType, userId);
  const ownerStorage = getSessionStorage(options?.storage);
  if (ownerStorage && (options?.expectedOwnerId === undefined || safeGet(ownerStorage, key) === options.expectedOwnerId)) {
    safeRemove(ownerStorage, key);
  }

  const legacyStorage = getLegacyOwnerStorage(options?.legacyStorage);
  if (legacyStorage && legacyStorage !== ownerStorage && (options?.expectedOwnerId === undefined || safeGet(legacyStorage, key) === options.expectedOwnerId)) {
    safeRemove(legacyStorage, key);
  }
}

export function readDraft<TData, TMeta = unknown>(
  key: string,
  options?: { ttlMs?: number; now?: number | Date; storage?: Storage | null; onStorageError?: () => void },
): DraftEnvelope<TData, TMeta> | null {
  const storage = getStorage(options?.storage);
  if (!storage) {
    options?.onStorageError?.();
    return null;
  }

  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    options?.onStorageError?.();
    return null;
  }

  if (!raw) return null;

  const parsed = safeJsonParse(raw);
  if (!isObject(parsed)) {
    safeRemove(storage, key);
    return null;
  }

  const v = parsed.v;
  const savedAt = parsed.savedAt;
  const url = parsed.url;

  if (typeof v !== 'number' || typeof savedAt !== 'string' || typeof url !== 'string' || !('data' in parsed)) {
    safeRemove(storage, key);
    return null;
  }

  const savedAtMs = Date.parse(savedAt);
  if (!Number.isFinite(savedAtMs)) {
    safeRemove(storage, key);
    return null;
  }

  const ttlMs = options?.ttlMs ?? DEFAULT_DRAFT_TTL_MS;
  const ageMs = nowMs(options?.now) - savedAtMs;
  if (ageMs > ttlMs) {
    safeRemove(storage, key);
    return null;
  }

  return parsed as unknown as DraftEnvelope<TData, TMeta>;
}

export function writeDraft<TData, TMeta = unknown>(
  key: string,
  data: TData,
  url: string,
  options?: {
    version?: number;
    meta?: TMeta;
    now?: number | Date;
    storage?: Storage | null;
    ttlMs?: number;
    prefix?: string;
  },
): boolean {
  const storage = getStorage(options?.storage);
  if (!storage) return false;

  const envelope: DraftEnvelope<TData, TMeta> = {
    v: options?.version ?? DEFAULT_DRAFT_VERSION,
    savedAt: new Date(nowMs(options?.now)).toISOString(),
    url,
    data,
    ...(options?.meta !== undefined ? { meta: options.meta } : {}),
  };

  const raw = safeJsonStringify(envelope);
  if (raw === null) {
    return false;
  }

  try {
    storage.setItem(key, raw);
    return true;
  } catch (error) {
    if (!isQuotaExceededError(error)) return false;

    // Best-effort cleanup and one retry.
    try {
      evictExpiredDrafts({
      storage,
      ...(options?.ttlMs !== undefined ? { ttlMs: options.ttlMs } : {}),
      ...(options?.now !== undefined ? { now: options.now } : {}),
      ...(options?.prefix !== undefined ? { prefix: options.prefix } : {}),
      });
    } catch {
      return false;
    }

    try {
      storage.setItem(key, raw);
      return true;
    } catch {
      return false;
    }
  }
}

export function clearDraft(key: string, options?: { storage?: Storage | null }): boolean {
  const storage = getStorage(options?.storage);
  if (!storage) return false;
  return safeRemove(storage, key);
}

export function evictExpiredDrafts(options?: {
  ttlMs?: number;
  now?: number | Date;
  prefix?: string;
  storage?: Storage | null;
}): number {
  const storage = getStorage(options?.storage);
  if (!storage) return 0;

  const ttlMs = options?.ttlMs ?? DEFAULT_DRAFT_TTL_MS;
  const prefix = options?.prefix ?? DRAFT_STORAGE_PREFIX;
  const now = nowMs(options?.now);
  let removed = 0;

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (!key || !key.startsWith(prefix)) continue;

    let raw: string | null;
    try {
      raw = storage.getItem(key);
    } catch {
      continue;
    }
    if (!raw) continue;

    const parsed = safeJsonParse(raw);
    if (!isObject(parsed) || typeof parsed.savedAt !== 'string') {
      safeRemove(storage, key);
      removed += 1;
      continue;
    }

    const savedAtMs = Date.parse(parsed.savedAt);
    if (!Number.isFinite(savedAtMs) || now - savedAtMs > ttlMs) {
      safeRemove(storage, key);
      removed += 1;
    }
  }

  return removed;
}
